/**
 * E007-T025: WorkflowSchema Component
 *
 * Displays the schema definition of a workflow.
 * Shows required/optional input fields and output fields.
 */

import { Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  AttachmentIcon,
  CheckBoxCheckLinearIcon,
  DateTimeIcon,
  InformationIcon,
  NumberIcon,
  SelectIcon,
  TextIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';

import { WorkflowService } from '../../services/workflow';
import type {
  WorkflowSchema,
  WorkflowSchemaField,
} from '../../services/workflow-client';
import * as styles from '../automation-panel.css';
import { getTypeColor, TypeChip } from './type-chip';

// =============================================================================
// Type Icon Mapping
// =============================================================================

const TYPE_ICONS: Record<string, ReactNode> = {
  attachment: <AttachmentIcon />,
  text: <TextIcon />,
  'rich-text': <TextIcon />,
  number: <NumberIcon />,
  checkbox: <CheckBoxCheckLinearIcon />,
  select: <SelectIcon />,
  'multi-select': <SelectIcon />,
  date: <DateTimeIcon />,
};

// =============================================================================
// Schema Field Row
// =============================================================================

interface SchemaFieldRowProps {
  field: WorkflowSchemaField;
  isRequired?: boolean;
}

const SchemaFieldRow: FC<SchemaFieldRowProps> = ({ field, isRequired }) => {
  const icon = TYPE_ICONS[field.type] || <TextIcon />;
  const color = getTypeColor(field.type);

  return (
    <div className={styles.fieldRow}>
      <span style={{ color, display: 'flex', alignItems: 'center' }}>
        {icon}
      </span>
      <span className={styles.fieldLabel} style={{ flex: 1 }}>
        {field.field}
        {isRequired && (
          <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
        )}
      </span>
      <Tooltip content={field.description}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: 'auto',
          }}
        >
          <TypeChip type={field.type} small style={{ marginLeft: 0 }} />
          <InformationIcon
            style={{ width: '12px', height: '12px', color: '#9ca3af' }}
          />
        </span>
      </Tooltip>
    </div>
  );
};

// =============================================================================
// Output Field Row
// =============================================================================

interface OutputFieldRowProps {
  fieldName: string;
  fieldDef: { type: string; description: string };
}

const OutputFieldRow: FC<OutputFieldRowProps> = ({ fieldName, fieldDef }) => {
  const icon = TYPE_ICONS[fieldDef.type] || <TextIcon />;
  const color = getTypeColor(fieldDef.type);

  return (
    <div className={styles.fieldRow}>
      <span style={{ color, display: 'flex', alignItems: 'center' }}>
        {icon}
      </span>
      <span className={styles.fieldLabel} style={{ flex: 1 }}>
        {fieldName}
      </span>
      <Tooltip content={fieldDef.description}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: 'auto',
          }}
        >
          <TypeChip type={fieldDef.type} small style={{ marginLeft: 0 }} />
          <InformationIcon
            style={{ width: '12px', height: '12px', color: '#9ca3af' }}
          />
        </span>
      </Tooltip>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

interface WorkflowSchemaDisplayProps {
  workflowId: string;
  /** If provided, uses this schema instead of fetching */
  schema?: WorkflowSchema | null;
  /** Show only input or output section */
  showOnly?: 'input' | 'output';
}

export const WorkflowSchemaDisplay: FC<WorkflowSchemaDisplayProps> = ({
  workflowId,
  schema: providedSchema,
  showOnly,
}) => {
  const t = useI18n();
  const workflowService = useService(WorkflowService);
  const selectedSchema = useLiveData(workflowService.selectedWorkflowSchema$);
  const schemaLoadingMap = useLiveData(workflowService.schemaLoading$);

  const isLoading = schemaLoadingMap.get(workflowId) ?? false;
  const schema = providedSchema ?? selectedSchema;

  // Load schema when component mounts or workflowId changes
  useEffect(() => {
    if (!providedSchema && workflowId) {
      workflowService.loadWorkflowSchema(workflowId).catch(() => {
        // Schema loading errors are handled in the service
      });
    }
  }, [workflowId, providedSchema, workflowService]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.fieldRow}>
        <span style={{ color: '#6b7280', fontSize: '13px' }}>
          Loading schema...
        </span>
      </div>
    );
  }

  // No schema available
  if (!schema) {
    return (
      <div
        className={styles.fieldRow}
        style={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '4px',
        }}
      >
        <span style={{ color: '#6b7280', fontSize: '13px' }}>
          No schema available
        </span>
        <span style={{ color: '#9ca3af', fontSize: '11px' }}>
          This workflow does not define input/output requirements.
        </span>
      </div>
    );
  }

  const hasRequired = schema.input.required.length > 0;
  const hasOptional = schema.input.optional.length > 0;
  const hasOutput = Object.keys(schema.output).length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Schema Header */}
      {schema.description && (
        <div
          style={{
            fontSize: '12px',
            color: '#6b7280',
            padding: '8px 12px',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: '6px',
          }}
        >
          {schema.description}
        </div>
      )}

      {/* Input Section */}
      {(!showOnly || showOnly === 'input') && (hasRequired || hasOptional) && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {t['com.streamlain.streamlain.schema.expected-input']()}
          </div>

          {/* Required Fields */}
          {hasRequired && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              {schema.input.required.map((field: WorkflowSchemaField) => (
                <SchemaFieldRow key={field.field} field={field} isRequired />
              ))}
            </div>
          )}

          {/* Optional Fields */}
          {hasOptional && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              {hasRequired && (
                <div
                  style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    marginTop: '8px',
                    marginBottom: '4px',
                  }}
                >
                  Optional
                </div>
              )}
              {schema.input.optional.map((field: WorkflowSchemaField) => (
                <SchemaFieldRow key={field.field} field={field} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Output Section */}
      {(!showOnly || showOnly === 'output') && hasOutput && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {t['com.streamlain.streamlain.schema.returns']()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(
              Object.entries(schema.output) as Array<
                [string, { type: string; description: string }]
              >
            ).map(([fieldName, fieldDef]) => (
              <OutputFieldRow
                key={fieldName}
                fieldName={fieldName}
                fieldDef={fieldDef}
              />
            ))}
          </div>
        </div>
      )}

      {/* Version Info */}
      <div
        style={{
          fontSize: '10px',
          color: '#9ca3af',
          textAlign: 'right',
        }}
      >
        Schema v{schema.version}
      </div>
    </div>
  );
};
